import type {
  AdminAnalytics,
  AdminMeetingView,
  AdminUser,
  ClientErrorsReport,
  DailyReport,
  DevDeployment,
  DevEngine,
  DevRequest,
  DevRequestTargetEnv,
  DevRequestType,
  DocumentRecord,
  HintStrategy,
  Invite,
  ListPendingToolsResponse,
  MarketplaceTool,
  PluginAction,
  PluginDataScope,
  PluginSurface,
  ProductFeedbackReport,
  ReleaseNote,
  SystemReview,
  UserStatus,
  UserTier,
  VoiceOfFieldReport,
} from "@scoach/types";
import { getIdToken } from "../../lib/firebase.ts";
import { api } from "../../lib/http.ts";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : (import.meta.env.VITE_API_BASE_URL as string);

/** Authenticated blob download (window.open would drop the auth header → 401). */
async function downloadBlob(path: string): Promise<Blob> {
  const token = await getIdToken().catch(() => null);
  const headers: Record<string, string> = {};
  if (token) headers["X-Authorization"] = `Bearer ${token}`;
  else if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Export failed: HTTP ${res.status}${text ? ` — ${text.slice(0, 200)}` : ""}`);
  }
  return res.blob();
}

export const adminApi = {
  fetchUsers: () => api<{ users: AdminUser[] }>("/admin/users").then((r) => r.users ?? []),

  fetchAnalytics: () => api<AdminAnalytics>("/admin/analytics"),

  setUserStatus: (uid: string, status: UserStatus) =>
    api<{ uid: string; status: UserStatus }>(`/admin/users/${uid}/status`, {
      method: "PATCH",
      body: { status },
    }),

  setUserSharedKey: (uid: string, useSharedKey: boolean) =>
    api<{ uid: string; useSharedKey: boolean }>(`/admin/users/${uid}/shared-key`, {
      method: "PATCH",
      body: { useSharedKey },
    }),

  setUserInterviewAccess: (uid: string, interviewAccess: boolean) =>
    api<{ uid: string; interviewAccess: boolean }>(`/admin/users/${uid}/interview-access`, {
      method: "PATCH",
      body: { interviewAccess },
    }),

  setUserFullAccess: (uid: string, fullAccess: boolean) =>
    api<{ uid: string; fullAccess: boolean }>(`/admin/users/${uid}/full-access`, {
      method: "PATCH",
      body: { fullAccess },
    }),

  setUserTier: (uid: string, tier: UserTier) =>
    api<{ uid: string; tier: UserTier }>(`/admin/users/${uid}/tier`, {
      method: "PATCH",
      body: { tier },
    }),

  setUserTitle: (uid: string, title: string) =>
    api<{ uid: string; title: string }>(`/admin/users/${uid}/title`, {
      method: "PATCH",
      body: { title },
    }),

  setUserHintPrefs: (
    uid: string,
    prefs: { hintStrategy?: HintStrategy; comparisonMode?: "aggressive" | "conservative" },
  ) =>
    api<{ uid: string }>(`/admin/users/${uid}/hint-prefs`, {
      method: "PATCH",
      body: prefs,
    }),

  bulkUpdateProfiles: (
    rows: { email: string; name?: string; title?: string; location?: string }[],
  ) =>
    api<{ updated: number; matched: string[]; unmatched: string[] }>("/admin/users/bulk-profile", {
      method: "POST",
      body: { rows },
    }),

  getMailerStatus: () =>
    api<{
      activeProvider: "gmail" | "resend" | "sender" | "none";
      senderEmail: string;
      senderName: string;
      hasGmail: boolean;
      hasResend: boolean;
      hasSenderNet: boolean;
      lastTestStatus?: { success: boolean; message: string; timestamp: string };
      updatedAt?: string;
    }>("/admin/mailer/status"),

  connectGmailMailer: (code: string, redirectUri?: string) =>
    api<{ ok: boolean; senderEmail: string; provider: string }>("/admin/mailer/connect-gmail", {
      method: "POST",
      body: { code, redirectUri },
    }),

  saveMailerConfig: (config: { resendApiKey?: string; senderEmail?: string; senderName?: string }) =>
    api<{ ok: boolean }>("/admin/mailer/config", {
      method: "POST",
      body: config,
    }),

  sendTestEmail: (to: string) =>
    api<{ ok: boolean; message: string }>("/admin/mailer/send-test", {
      method: "POST",
      body: { to },
    }),

  fetchInvites: () => api<{ invites: Invite[] }>("/admin/invites").then((r) => r.invites ?? []),

  inviteUser: (email: string, useSharedKey?: boolean) =>
    api<Invite>("/admin/invites", { method: "POST", body: { email, useSharedKey } }),

  revokeInvite: (email: string) =>
    api<{ ok: boolean }>(`/admin/invites/${encodeURIComponent(email)}`, { method: "DELETE" }),

  fetchUserMeetings: (uid: string) =>
    api<{ meetings: AdminMeetingView[] }>(`/admin/users/${uid}/meetings`).then((r) => r.meetings),

  fetchReviews: () =>
    api<{ reviews: SystemReview[] }>("/admin/reviews").then((r) => r.reviews ?? []),

  fetchDailyReport: (date?: string) =>
    api<{ report: DailyReport | null }>("/admin/daily-report", {
      query: date ? { date } : undefined,
    }).then((r) => r.report),

  fetchDailyReports: () =>
    api<{ reports: DailyReport[] }>("/admin/daily-reports").then((r) => r.reports ?? []),

  runDailyReport: (date?: string) =>
    api<{ report: DailyReport }>("/admin/daily-report/run", {
      method: "POST",
      body: { date },
    }).then((r) => r.report),

  // --- Voice of the field (client bugs/feature requests by product) ---
  fetchVoiceOfField: () =>
    api<{ report: VoiceOfFieldReport | null }>("/admin/voice-of-field").then((r) => r.report),

  runVoiceOfField: () =>
    api<{ report: VoiceOfFieldReport }>("/admin/voice-of-field/run", {
      method: "POST",
      body: {},
    }).then((r) => r.report),

  exportVoiceOfFieldXlsx: () => downloadBlob("/admin/voice-of-field.xlsx"),
  exportVoiceOfFieldPdf: () => downloadBlob("/admin/voice-of-field.pdf"),

  // --- Product feedback (deeper per-product analysis for Google PMs) ---
  fetchProductFeedback: () =>
    api<{ report: ProductFeedbackReport | null }>("/admin/product-feedback").then((r) => r.report),

  runProductFeedback: () =>
    api<{ report: ProductFeedbackReport }>("/admin/product-feedback/run", {
      method: "POST",
      body: {},
    }).then((r) => r.report),

  exportProductFeedbackXlsx: () => downloadBlob("/admin/product-feedback.xlsx"),

  // --- User bugs (client errors) ---
  fetchClientErrors: (days: number, source: "prod" | "qc" | "ads" | "current" = "prod") =>
    api<ClientErrorsReport>("/admin/client-errors", { query: { days, source } }),

  resolveClientError: (
    signature: string,
    resolved: boolean,
    source: "prod" | "qc" | "ads" | "current",
  ) =>
    api<{ ok: boolean }>("/admin/client-errors/resolve", {
      method: "POST",
      body: { signature, resolved, source },
    }),

  // --- Self-serve dev agent (dev admins: Moshe + Avi) ---
  fetchDevRequests: () =>
    api<{ requests: DevRequest[] }>("/admin/dev-requests").then((r) => r.requests ?? []),

  createDevRequest: (
    type: DevRequestType,
    title: string,
    description: string,
    targetEnv: DevRequestTargetEnv = "qc",
    engine: DevEngine = "gemini-3.8-flash",
  ) =>
    api<{ id: string; status: string }>("/admin/dev-requests", {
      method: "POST",
      body: { type, title, description, targetEnv, engine },
    }),

  approveDevPlan: (id: string) =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/approve-plan`, { method: "POST", body: {} }),

  reviseDevPlan: (id: string, text?: string) =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/revise-plan`, {
      method: "POST",
      body: { text },
    }),

  reviseDevBuild: (id: string, text?: string) =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/revise-build`, {
      method: "POST",
      body: { text },
    }),

  addDevComment: (id: string, text: string, kind: "plan-feedback" | "build-feedback" | "note") =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/comments`, {
      method: "POST",
      body: { text, kind },
    }),

  approveDevProd: (id: string) =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/approve-prod`, { method: "POST", body: {} }),

  rejectDevRequest: (id: string, reason?: string) =>
    api<{ ok: boolean }>(`/admin/dev-requests/${id}/reject`, { method: "POST", body: { reason } }),

  fetchDevDeployments: (env: DevRequestTargetEnv) =>
    api<{ deployments: DevDeployment[] }>("/admin/dev-deployments", { query: { env } }).then(
      (r) => r.deployments ?? [],
    ),

  rollbackDeployment: (env: DevRequestTargetEnv, gitSha: string) =>
    api<{ ok: boolean }>("/admin/dev-deployments/rollback", {
      method: "POST",
      body: { env, gitSha },
    }),

  fetchDocuments: () =>
    api<{ documents: DocumentRecord[] }>("/admin/documents").then((r) => r.documents ?? []),

  uploadDocument: (file: File) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    return api<DocumentRecord>("/admin/documents", { method: "POST", body: fd });
  },

  addDocumentLink: (url: string, googleToken?: string) =>
    api<DocumentRecord>("/admin/documents/link", {
      method: "POST",
      body: { url },
      // Pass the freshly obtained token explicitly to avoid any store-timing race.
      headers: googleToken ? { "X-Drive-Token": googleToken } : undefined,
    }),

  deleteDocument: (id: string) =>
    api<{ ok: boolean }>(`/admin/documents/${id}`, { method: "DELETE" }),

  // What's New (release note) moderation: list managed notes, edit text, approve.
  fetchWhatsNew: () =>
    api<{ notes: (ReleaseNote & { approved: boolean })[] }>("/admin/whats-new").then(
      (r) => r.notes ?? [],
    ),
  editRelease: (id: string, patch: { title?: string; items?: string[] }) =>
    api<{ ok: boolean }>("/admin/whats-new/edit", { method: "POST", body: { id, ...patch } }),
  setReleaseApproval: (id: string, approved: boolean) =>
    api<{ approvedIds: string[] }>("/admin/whats-new/approve", {
      method: "POST",
      body: { id, approved },
    }).then((r) => r.approvedIds ?? []),

  // --- Marketplace tool-suggestion review ---
  fetchPendingTools: () =>
    api<ListPendingToolsResponse>("/api/marketplace/admin/pending").then((r) => r.tools ?? []),

  approveTool: (id: string) =>
    api<{ ok: boolean }>(`/api/marketplace/admin/${id}/approve`, { method: "POST", body: {} }),

  rejectTool: (id: string, reason?: string) =>
    api<{ ok: boolean }>(`/api/marketplace/admin/${id}/reject`, {
      method: "POST",
      body: { reason },
    }),

  // --- Developer plugins ---
  fetchPendingPlugins: () =>
    api<{ plugins: MarketplaceTool[] }>("/api/marketplace/admin/plugins-pending").then(
      (r) => r.plugins ?? [],
    ),
  rescanPlugin: (id: string) =>
    api<{ ok: boolean }>(`/api/marketplace/admin/plugins/${id}/rescan`, { method: "POST" }),
  approvePlugin: (
    id: string,
    body: { dataScope?: PluginDataScope; actions?: PluginAction[]; surfaces?: PluginSurface[] },
  ) =>
    api<{ ok: boolean }>(`/api/marketplace/admin/plugins/${id}/approve`, { method: "POST", body }),
  /** Approved public plugins — for the admin "remove from everyone" view. */
  fetchApprovedPlugins: () =>
    api<{ plugins: MarketplaceTool[] }>("/api/marketplace/admin/plugins-approved").then(
      (r) => r.plugins ?? [],
    ),
  /** Remove a plugin that was approved for everyone (stops it rendering for all). */
  removePlugin: (id: string) =>
    api<{ ok: boolean }>(`/api/marketplace/admin/plugins/${id}/remove`, { method: "POST" }),
};
